import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
  IntroPage,
  PaymentMethod,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { eq, gt } from '../ast/utils.js';
import STANDARD_COMPONENTS from './standards/index.js';
import { SpecialSectionKeys } from './standards/types.js';

const ZIPS = [
  //TOOD(SD): Maybe create separate util for this
  '19019',
  '19101',
  '19102',
  '19103',
  '19104',
  '19105',
  '19106',
  '19107',
  '19108',
  '19109',
  '19110',
  '19111',
  '19112',
  '19114',
  '19115',
  '19116',
  '19118',
  '19119',
  '19120',
  '19121',
  '19122',
  '19123',
  '19124',
  '19125',
  '19126',
  '19127',
  '19128',
  '19129',
  '19130',
  '19131',
  '19132',
  '19133',
  '19134',
  '19135',
  '19136',
  '19137',
  '19138',
  '19139',
  '19140',
  '19141',
  '19142',
  '19143',
  '19144',
  '19145',
  '19146',
  '19147',
  '19148',
  '19149',
  '19150',
  '19151',
  '19152',
  '19153',
  '19154',
  '19155',
  '19160',
  '19092',
  '19093',
  '19099',
  '19161',
  '19162',
  '19170',
  '19171',
  '19172',
  '19173',
  '19175',
  '19176',
  '19177',
  '19178',
  '19179',
  '19181',
  '19182',
  '19183',
  '19184',
  '19185',
  '19187',
  '19188',
  '19190',
  '19191',
  '19192',
  '19193',
  '19194',
  '19195',
  '19196',
  '19197',
  '19244',
  '19255',
];

export const QUESTIONS: { [key: string]: ApplicationQuestion } = {
  [CommonQuestionKeys.ZIP_CODE]: {
    key: CommonQuestionKeys.ZIP_CODE,
    copy: { title: 'What is your ZIP code?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: CommonQuestionKeys.ZIP_CODE,
        copy: 'Zip Code',
        options: ZIPS.map((zip) => ({ label: zip, value: zip })),
        props: { searchable: true },
      },
    ],
  },
  [CommonQuestionKeys.ADDRESS]: {
    key: CommonQuestionKeys.ADDRESS,
    copy: { title: 'What is the address of your FCC home?' },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.ADDRESS,
      },
    ],
  },
  childcareHomeLicense: {
    key: 'childcareHomeLicense',
    copy: {
      title:
        'Are you currently licensed to operate a family child care home in the state of Pennsylvania?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'childcareHomeLicense',
        validation: {
          required: true,
          rules: [
            {
              rule: eq('childcareHomeLicense', 'yes'),
              message:
                'You must be licensed to operate a family child care home in order to continue the application',
            },
          ],
        },
        options: YesNoOptions,
      },
    ],
  },
  mpiNumber: {
    key: 'mpiNumber',
    copy: {
      title: `Please enter your Master Provider Index (MPI) number. 
       An MPI number can be found on the bottom of the provider’s Certificate of Compliance issued by the Pennsylvania DHS. 
       Your MPI Number will be verified quarterly.`,
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'mpiNumber',
        copy: 'Master Provider Index (MPI) Number',
      },
    ],
  },
  fccProgram: {
    key: 'fccProgram',
    copy: { title: 'What is the name of your FCC program? ' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'fccProgram',
        copy: 'FCC program name',
      },
    ],
  },
  childcareHoursPerWeek: {
    key: 'childcareHoursPerWeek',
    copy: {
      title:
        'Please enter the number of hours per week you provide child care services for children in your care or your hours of operation.',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'hoursPerWeek',
        copy: 'Hours per week',
      },
    ],
  },
  caringForChildren: {
    key: 'caringForChildren',
    copy: { title: 'How many children do you currently care for and receive income for?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'numberOfChildren',
        copy: 'Number of children',
      },
      {
        type: FieldType.Typography,
        key: 'numberOfChildren.question',
        dynamicLogic: gt('numberOfChildren', 0),
        copy: 'How many of these children are under the age of 5?',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'numberOfChildrenUnder5',
        dynamicLogic: gt('numberOfChildren', 0),
        copy: 'Number of children',
      },
    ],
  },
  childCareSubsides: {
    key: 'childcareSubsides',
    copy: {
      title:
        'Do you currently receive child care subsidies (Child Care Works) as a form of payment for any of the children in your care?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'childcareSubsides',
        options: YesNoOptions,
      },
    ],
  },
  starRating: {
    key: 'starRating',
    copy: {
      title:
        'What is your STAR rating?  Keystone STARS is Pennsylvania’s Quality Rating and Improvement System (QRIS).',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'starRating',
        copy: 'STAR rating',
        options: [
          { value: 'star-1', label: 'Star 1' },
          { value: 'star-2', label: 'Star 2' },
          { value: 'star-3', label: 'Star 3' },
          { value: 'star-4', label: 'Star 4' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'starRating.question',
        dynamicLogic: eq('starRating', 'other'),
        copy: 'Please specify below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'otherStarRating',
        dynamicLogic: eq('starRating', 'other'),
        copy: 'STAR Rating',
      },
    ],
  },
  agreesToReport: {
    key: 'agreesToReport',
    copy: {
      title:
        'If selected to participate in the TPP and if your caregiving situation changes during the program, do you agree to report when requested and provide updates?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'agreesToReport',
        options: YesNoOptions,
      },
    ],
  },
  applyingPublicAssistance: {
    key: 'applyingPublicAssistance',
    copy: {
      title:
        'Do you currently receive or are you in the process of applying for any public assistance (SNAP, TANF, Supplemental Security Income (SSI), etc)?',
      intro: 'Your response to this question will not affect your eligibility to apply.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'applyingPublicAssistance',
        options: YesNoOptions,
      },
    ],
  },
  understandsEligibilityRisk: {
    key: 'understandsEligibilityRisk',
    dynamicLogic: eq('applyingPublicAssistance', 'yes'),
    copy: {
      title:
        'If accepted into this program, do you understand that there is a risk that the cash you will receive may impact your eligibility for public assistance (benefits)? ',
      intro: `Public assistance (benefits) are forms of assistance from the government. Public benefits include programs such as Supplemental Security Income (SSI), the Supplemental Nutrition Assistance Program (SNAP), Medical Assistance, Child Care Works (CCW), and others.  \n\n
The program you are applying for, the Thriving Providers Project (TPP), is working closely with local and state government agencies to protect as many benefits as possible from being impacted by the cash assistance you may receive from this program. \n\n  
However, it is possible that your eligibility for certain public benefits may be impacted if you are selected and agree to participate in this program. \n\n  
If selected for this program, you can choose to participate in a free information session to learn about potential impacts to your current public assistance (benefits), or public assistance (benefits) you plan to apply for (see question 20). \n\n 
If you have further questions about the program, reach out to [thrivingprovidersproject@phmc.org](mailto:thrivingprovidersproject@phmc.org) or (267)-314-1398.  \n\n
Please check “Yes” to continue with your application noting that you understand there are possible risks to your benefits associated with participating in a direct cash assistance program.`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'understandsEligibilityRisk',
        options: YesNoOptions,
      },
    ],
  },
  freeInformationSession: {
    key: 'freeInformationSession',
    // TODO(SD): Need to apply dynamic logic here?
    copy: {
      title:
        'If accepted into this program, would you like to participate in a free information session to help you understand any potential impact to your public benefits due to the cash assistance you will receive?',
      intro: `During the information session, you will learn about public benefits that may be impacted by cash assistance from this program, public benefits that will not be impacted by the cash assistance from this program, and your reporting responsibilities for your public benefits as they relate to your participation in this program. \n\n
To participate in a free information session, we will need to share your contact and benefits information with a trained benefits counselor.
Please check “Yes" if you want to participate in a free information session to help you understand any potential impacts to your public benefits from the cash assistance you may receive from this program. \n\n
Please check “No” if you do NOT want to participate in a free information session to help you understand any potential impacts to your public benefits from the cash assistance you will receive from this program. \n\n
If you change your mind, you can opt-in at any time during your participation in the program by contacting [thrivingprovidersproject@phmc.org](mailto:thrivingprovidersproject@phmc.org) or 267-314-1398.`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'participateInfoSession',
        options: YesNoOptions,
      },
    ],
  },
  preferredLanguage: {
    key: 'preferredLanguage',
    copy: {
      title:
        'What is your preferred language of communication for written documents (i.e. SMS messages, forms, etc.)?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'preferredLanguage',
        options: [
          { label: 'English', value: 'english' },
          { label: 'Spanish', value: 'spanish' },
          { label: 'Arabic', value: 'arabic' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'preferredLanguage.question',
        dynamicLogic: eq('preferredLanguage', 'other'),
        copy: 'Please specify below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'otherPreferredLanguage',
        dynamicLogic: eq('preferredLanguage', 'other'),
        copy: 'Preferred Language',
      },
    ],
  },
  preferredPhoneNumber: {
    key: 'preferredPhoneNumber',
    copy: {
      title: 'What is your preferred phone number to contact you on?',
      intro: `Please provide your country code, area code, and phone number. This is 
where important information about the program will be sent, so please select a number 
you regularly have access to and can receive SMS messages on.`,
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        key: 'preferredPhoneNumber',
        copy: 'Phone Number',
        props: { helperText: 'Example: +1 (219) 873-2341' },
        validation: { required: false },
      },
    ],
  },
  preferredEmailAddress: {
    key: 'preferredEmailAddress',
    copy: {
      title: 'What is your preferred email address?',
      intro:
        'This is where important information about the program will be sent, so please make sure you regularly have access to this email.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'preferSecondaryEmail',
        options: [
          { label: '{APPLICANT_EMAIL}', value: 'no' },
          { label: 'Other', value: 'yes' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'preferredEmailAddress.question2',
        dynamicLogic: eq('preferSecondaryEmail', 'yes'),
        copy: 'Please provide your preferred email address below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        key: 'preferredEmailAddress',
        dynamicLogic: eq('preferSecondaryEmail', 'yes'),
        copy: 'Email Address',
      },
    ],
  },
  preferredContactMethod: {
    key: 'preferredContactMethod',
    copy: {
      title: 'How would you prefer we contact you?',
      intro: 'Select one from the dropdown menu:',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'contactMethod',
        copy: 'Preferred contact method',
        options: [
          { value: 'phone', label: 'Phone' },
          { value: 'sms', label: 'Text/SMS Messages' },
          { value: 'email', label: 'Email ' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'contactMethod.question',
        dynamicLogic: eq('contactMethod', 'other'),
        copy: 'Please specify below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'preferredContactMethod',
        dynamicLogic: eq('contactMethod', 'other'),
        copy: 'Contact method',
      },
    ],
  },

  preferredPaymentMethod: {
    key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
    copy: { title: 'If selected to participate, what is your preferred method of payment?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
        options: [
          {
            label: 'ACH/Direct Deposit',
            description:
              'We recommend using this option if you have access to a bank account. You should receive your funds in 1-3 business days.',
            value: PaymentMethod.DirectDeposit,
          },
          {
            label: 'Physical Prepaid Debit Card',
            description:
              'Physical cards will take longer than the other payment methods. This can take more than 10 business days to receive.',
            value: PaymentMethod.PhysicalCard,
          },
          {
            label: 'Virtual Prepaid Debit Card',
            description:
              'Virtual cards are a faster method to receive your funds. This should take less than 3 business days.',
            value: PaymentMethod.VirtualCard,
          },
          {
            label: 'I’m not sure',
            description:
              'We will follow up with you to decide on the best way for you to receive payment.',
            value: 'not-sure',
          },
        ],
      },
    ],
  },

  applicationAssistanceHelper: {
    key: 'submission.assistance.signature',
    copy: { title: 'Did you receive assistance completing this application?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'submission.assistance.receivedAssistance',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'submission.assistance.question',
        dynamicLogic: eq('submission.assistance.receivedAssistance', 'yes'),
        copy: 'Please enter the case manager’s name/digital signature below:',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: CommonQuestionKeys.SIGNATURE,
        dynamicLogic: eq('submission.assistance.receivedAssistance', 'yes'),
        copy: 'Digital Signature',
        props: { helperText: 'Ex: Micha Lynn Johnson' },
      },
    ],
  },
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: { title: 'Please confirm this statement:' },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: 'I attest that the information I provided in this application is accurate to the best of my knowledge.',
      },
    ],
  },
};

const SECTIONS: ApplicationSection[] = [
  {
    key: 'personalInfo',
    name: 'Personal Info',
    overview: {
      title: 'Let’s start with some personal information about you',
      description: 'In this section, you’ll let us know about your home address and date of birth.',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'Personal Info',
        questions: [
          QUESTIONS[CommonQuestionKeys.ZIP_CODE],
          QUESTIONS[CommonQuestionKeys.ADDRESS],
          STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.DATE_OF_BIRTH],
        ],
      },
    ],
  },
  {
    key: 'providerInfo',
    name: 'Provider Info',
    overview: {
      title: 'Now, let’s learn about your provider information',
      description:
        'In this section, you’ll let us know about your eligibility by answering specific questions about your current situation.',
    },
    questionGroups: [
      {
        key: 'providerInfo',
        name: 'Provider Info',
        questions: [
          QUESTIONS.childcareHomeLicense,
          QUESTIONS.mpiNumber,
          QUESTIONS.fccProgram,
          QUESTIONS.childcareHoursPerWeek,
          QUESTIONS.caringForChildren,
          QUESTIONS.childCareSubsides,
          QUESTIONS.starRating,
          QUESTIONS.agreesToReport,
          QUESTIONS.applyingPublicAssistance,
          QUESTIONS.understandsEligibilityRisk,
          QUESTIONS.freeInformationSession,
        ],
      },
    ],
  },
  {
    key: 'preferences',
    name: 'Preferences',
    overview: {
      description: `
In this section, you’ll share your preferred language of communication for written
documents, phone number, email address, and method of payment.`,
      title: 'Now it’s time to find out a little more about your preferences',
    },
    questionGroups: [
      {
        key: 'preferences',
        name: 'Preferences',
        questions: [
          QUESTIONS.preferredLanguage,
          QUESTIONS.preferredPhoneNumber,
          QUESTIONS.preferredEmailAddress,
          QUESTIONS.preferredContactMethod,
          QUESTIONS.preferredPaymentMethod,
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        overview: {
          description:
            'Please make sure all the information you provided is correct, so we can review your application.',
        },
        questionGroups: [
          // Drop the first question and replace it with this one
          { questions: [null, QUESTIONS.applicationAssistanceHelper] },
          // Drop the first question and replace it with this one
          { questions: [null, QUESTIONS.affirmation] },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is the Thriving Providers Project?',
      body: `PHMC is participating in the Thriving Providers Project, which provides 
direct cash assistance that supports home-based child care providers. These funds do not 
require repayment. If direct cash assistance could help you pay for your needs, you may be 
eligible to submit an application through PHMC.`,
    },
    {
      key: 'eligibility-disclaimer',
      layout: 'welcome',
      title: 'Before you begin, please review the program eligibility criteria',
      body: ` 
  - Must currently and in the foreseeable future be a home-based child care provider who earns income by caring for children.  
  - Only providers invited to apply will be considered.  
  - Must currently live in Philadelphia, Pennsylvania.  
  - Must be 18 years old.  
  - Must be a licensed family child care provider by the state of Pennsylvania in the city of Philadelphia.  
  - Must be operating a FCC home a minimum of 20 hours a week.`,
    },
    {
      key: 'stanford-study',
      layout: 'default',
      title: 'Stanford Center on Early Childhood',
      body: `If selected as a Thriving Providers Project (TPP) participant, you will be invited to participate in an evaluation with the Stanford Center on Early Childhood, 
      that aims to learn more about how TPP can support the needs and well-being of home-based child care providers, the children they care for, and their families.  \n\n
      
  The evaluation includes brief, compensated surveys about your experiences. This information will be used by the TPP team to learn about how, 
  and in what ways, the program is helpful—this will allow them to continue improving the program, with the goal of potentially offering it to even more child care providers in the future. 
  More details will be provided to selected participants during enrollment.`,
    },
    mergeObjects(
      [
        STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
        {
          steps: [
            undefined,
            {
              title: 'It takes around 15 minutes to complete',
              description:
                'You can expect to spend 15 minutes or less filling out your application.',
            },
            undefined,
            undefined,
            {
              title: 'Get a decision',
              icon: 'mail',
              description:
                'We will review your application and notify you by email of the decision as soon as possible.',
            },
          ],
        } as IntroPage,
      ],
      'merge',
    ),
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        message:
          'You could be found eligible to receive direct cash assistance up to {MAX_AMOUNT}. These funds do not require repayment and are intended to help home-based child care providers.',
      },
      {
        status: 'InProgress',
        message:
          'You’ve made great progress so far! Keep working on your TPP registration so you can claim your TP incentives!',
      },
    ],
  },
  sections: SECTIONS,
};

export default CONFIG;
